import type { Locale } from "date-fns";
import { format } from "date-fns";
import { de, enUS, es, fr } from "date-fns/locale";
import i18n from "i18n";

const PARIS = "Europe/Paris";

export const MAINTENANCE_TIME_ZONE = PARIS;

const dateFnsLocales: Record<string, Locale> = { fr, en: enUS, es, de };

const dateLocale = () => dateFnsLocales[i18n.language] || fr;

const datePattern = () => (i18n.language === "en" ? "EEEE, MMMM d" : "EEEE d MMMM");

/** Wall clock in Europe/Paris, so date-fns prints Paris time rather than the browser zone. */
const parisWallClock = (date: Date): Date | null => {
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: PARIS,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
    }).formatToParts(date);
    const read = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((part) => part.type === type)?.value);
    const year = read("year");
    const month = read("month");
    const day = read("day");
    let hour = read("hour");
    const minute = read("minute");
    if ([year, month, day, hour, minute].some((part) => Number.isNaN(part))) return null;
    if (hour === 24) hour = 0;
    return new Date(year, month - 1, day, hour, minute);
};

const timeZoneOffsetMs = (instant: Date, timeZone: string): number => {
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone,
        hourCycle: "h23",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    }).formatToParts(instant);
    const read = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((part) => part.type === type)?.value);
    let hour = read("hour");
    if (hour === 24) hour = 0;
    const asUtc = Date.UTC(read("year"), read("month") - 1, read("day"), hour, read("minute"), read("second"));
    return asUtc - instant.getTime();
};

/** The date picker’s wall clock is Europe/Paris, whatever the browser zone is. */
export const parisWallClockToUtc = (wallClock: Date): Date => {
    const utcGuess = Date.UTC(
        wallClock.getFullYear(),
        wallClock.getMonth(),
        wallClock.getDate(),
        wallClock.getHours(),
        wallClock.getMinutes(),
        wallClock.getSeconds(),
    );
    const corrected = utcGuess - timeZoneOffsetMs(new Date(utcGuess), PARIS);
    return new Date(utcGuess - timeZoneOffsetMs(new Date(corrected), PARIS));
};

export type MaintenanceRange = {
    sameDay: boolean;
    /** Written day, e.g. "mercredi 29 octobre". */
    date: string;
    startDate: string;
    endDate: string;
    /** "HH:mm" in Europe/Paris. */
    start: string;
    end: string;
};

/** Written date and clock range in Europe/Paris. */
export const formatMaintenanceRange = (startsAt: string, endsAt: string): MaintenanceRange | null => {
    const startDate = new Date(startsAt);
    const endDate = new Date(endsAt);
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return null;
    const startWall = parisWallClock(startDate);
    const endWall = parisWallClock(endDate);
    if (!startWall || !endWall) return null;
    const locale = dateLocale();
    const pattern = datePattern();
    const writtenDate = (date: Date) => {
        const value = format(date, pattern, { locale });
        return value.charAt(0).toLocaleUpperCase(i18n.language) + value.slice(1);
    };
    const clock = (date: Date) => format(date, "HH:mm");
    return {
        sameDay: format(startWall, "yyyy-MM-dd") === format(endWall, "yyyy-MM-dd"),
        date: writtenDate(startWall),
        startDate: writtenDate(startWall),
        endDate: writtenDate(endWall),
        start: clock(startWall),
        end: clock(endWall),
    };
};
